import {
  ComputedRef,
  PropType,
  computed,
  defineComponent,
  inject,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { Store } from 'pinia'
import { Badge, Button, Checkbox, Popup } from 'vant'
import { showToast } from '@nio-fe/horo-h5'
import styles from './styles.module.scss'
import { FilterItem, FilterKey, FilterState } from './filter.model'
import { getFilterData } from './filter.api'
import { ServiceResponse } from 'loca-boot-core'
import { px2rem } from '@/utils/px2rem'
import { ChargeMapContext } from '../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import {
  DEFAULT_CITY_ZOOM_LEVEL,
  DEFAULT_COUNTRY_ZOOM_LEVEL,
  DEFAULT_DISTRICT_ZOOM_LEVEL,
  SHOW_THIRD_ZOOM_LEVEL,
} from '../../config/zoomLevelConfig'
import { MapViewMode } from '../../contract/map'

/**
 * 自动刷新和回到首页
 */
export type FilterProps = {
  zoomEnd: () => void
}
export default defineComponent({
  name: 'filter-view',
  components: {
    'van-checkbox': Checkbox,
    'van-badge': Badge,
    'van-button': Button,
  },
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
    xinfaView: {
      // 信发设备使用此组件,需要隐藏handleBox组件
      type: Boolean,
      default: false,
    },
  },
  emits: ['filterChanged', 'closePopup'],

  setup: (props, { emit, expose }) => {
    // 获取组件配置
    const componentsConfig: any = window.NSComponentsConfig
    if (!componentsConfig?.filter?.show) {
      return () => null
    }
    const client = inject<string>('client', '')
    const mapInstance = computed(() => props.mapInstance)
    const state = reactive({
      filterDataReady: false, // 筛选数据准备完毕
      filterIconSelectedIndex: -1,
      confirmFilterChange: false, // 筛选状态确认
      stashFilter: '',
      filterStateBeforeDisabled: FilterState.Off, // 缓存三方桩disabled前的状态
      filter: [] as FilterItem[], // 原始filter数据
      filterRawData: '',
      stopEmitFilterChanged: false, // 通过使用bus触发的triggerFilter， 不然从详情跳到一个没有展现的icon会冲突，触发点变小
      thirdSourceIndex: [-1, -1, -1],
      isShow: false, // Popup显示隐藏,
    })
    const zoomLevel = ref<number>(0)
    let powerResourcesIndex = -1
    let resourceIndex = -1
    let thirdChargeStationIndex = -1

    const selectedNumList: ComputedRef<number[]> = computed(() => {
      const numbs: number[] = []
      state.filter?.forEach((item: FilterItem) => {
        let counter = 0
        item.options?.forEach((option: FilterItem) => {
          option.options?.forEach((option: FilterItem) => {
            if (option.filterState === FilterState.On) {
              counter++
            }
          })
        })
        numbs.push(counter)
      })
      return numbs
    })

    /**
     * 找出三方点的index
     */
    const findThirdIndex = () => {
      // 找到三方点的index
      state.filter.forEach((item: FilterItem, index: number) => {
        if (item.key === FilterKey.Recharge || item.key === FilterKey.RechargeCS) {
          powerResourcesIndex = index
          item.options.forEach((option: FilterItem, index: number) => {
            if (option.key == FilterKey.ResourceTypes) {
              resourceIndex = index
              option.options.forEach((lastOption: FilterItem, index: number) => {
                if (lastOption.key == FilterKey.ThirdStation) {
                  thirdChargeStationIndex = index
                  return
                }
              })
              return
            }
          })
          return
        }
      })
      state.thirdSourceIndex = [powerResourcesIndex, resourceIndex, thirdChargeStationIndex]
      props.componentStore.bus.emit('initResourceFilter', {
        filter: state.filter,
        thirdSourceIndex: state.thirdSourceIndex,
      })
    }

    /**
     * 其他组件修改筛选勾选状态
     */
    props.componentStore.bus.on(
      'triggerFilter',
      (params: { pointType: string; trigger: boolean }) => {
        state.stopEmitFilterChanged = true
        const { pointType, trigger } = params
        state.filter.forEach((element: FilterItem, index: number) => {
          if (element.key === pointType) {
            element.selected = true
            state.filter[index].filterState = trigger ? FilterState.On : FilterState.Off
            // 重置下级筛选
            resetSecondFilter(index)
          }
        })
        state.filterStateBeforeDisabled = state.filter[powerResourcesIndex].options[resourceIndex]
          .options[thirdChargeStationIndex].filterState as FilterState // 三方桩保留原来筛选状态
      }
    )

    /**
     * 第一层级改变
     * @param checked
     * @param index
     */
    const firstLevelFilterChanged = (checked: boolean, index: number) => {
      closeFirstLevelFilterPan()
      checked && beforeSelectedChange(index)
      state.filter[index].filterState = checked ? FilterState.On : FilterState.Off
      if (checked) {
        state.filter[index].filterState = FilterState.On
      } else {
        state.filter[index].filterState = FilterState.Off
        state.filterStateBeforeDisabled = FilterState.Off // 重置三方桩的disabled前的状态
        // 重置下级筛选
        resetSecondFilter(index)
      }
      afterSelectedChange()
      onFilterChange()
    }

    /**
     * 若从全不选状态到只选一个，其他的都置为off
     */
    const beforeSelectedChange = (index: number) => {
      if (state.filter.filter((a, i) => a.selected && i !== index).length == 0) {
        state.filter.forEach((item: FilterItem) => {
          item.filterState = FilterState.Off
        })
      }
    }

    /**
     * 若一个都没选中，第一层的FilterState全部置为on
     */
    const afterSelectedChange = () => {
      if (state.filter.filter((a) => a.selected).length == 0) {
        state.filter.forEach((item: FilterItem) => {
          item.filterState = FilterState.On
        })
      }
    }

    /**
     * 点击第一层级筛选的icon
     */
    const openFirstLevelFilterPan = (index: number) => {
      if (disableFilterIconClick(index)) {
        showToast({
          content: '请放大到城市层级再进行筛选',
          type: 'failure',
        })
      } else if (state.filter.every((item) => !item.selected)) {
        // 筛选全部关闭下不允许点击筛选小icon
        return
      } else if (state.filter[index].filterState == FilterState.On) {
        state.filterIconSelectedIndex = index
        stashFilterFn(index) // 打开二级筛选前缓存筛选状态
      }
      // 若详情看板打开则关闭
      props.componentStore.detailPanel && props.componentStore.bus.emit('closeDetailPanel')
      // 若搜索看板打开则关闭
      props.componentStore.searchListPanel && props.componentStore.bus.emit('closeSearchListPanel')
      props.componentStore.triggerPanel('filterPanel') // 只打开筛选看板
    }

    /**
     * 缓存打开筛选看板前的筛选状态
     * 若没有点击确定按钮则还原筛选状态
     */
    const stashFilterFn = (index: number) => {
      if (index > -1) {
        state.stashFilter = JSON.stringify(state.filter[index].options)
      }
    }

    /**
     * 缓存打开筛选看板前的筛选状态
     * 若没有点击确定按钮则还原筛选状态
     */
    const restoreFilterFn = (index: number) => {
      if (index > -1) {
        state.filter[index].options = JSON.parse(state.stashFilter)
        // 若筛选还原后，若三方点的状态为disabled，则重置为off
        if (powerResourcesIndex > -1 && resourceIndex > -1 && thirdChargeStationIndex > -1) {
          const level = mapInstance.value?.getZoom()
          if (level <= DEFAULT_COUNTRY_ZOOM_LEVEL || level >= SHOW_THIRD_ZOOM_LEVEL) {
            if (
              state.filter[powerResourcesIndex].options[resourceIndex].options[
                thirdChargeStationIndex
              ].filterState == FilterState.Disabled
            ) {
              state.filter[powerResourcesIndex].options[resourceIndex].options[
                thirdChargeStationIndex
              ].filterState = FilterState.Off
            }
          }
        }
      }
    }

    props.componentStore.bus.on('resetSecondFilter', (key: string) => {
      const index = state.filter.findIndex((a) => a.key === key)
      if (index > -1) {
        resetSecondFilter(index)
      }
    })

    /**
     * 重置第二层的筛选
     * @param index 重置指定的 index
     */
    const resetSecondFilter = (index: number) => {
      if (index > -1) {
        // 重置指定的 index
        state.filter[index]?.options?.forEach((option: FilterItem) => {
          option.options?.forEach((option: FilterItem) => {
            option.filterState = FilterState.Off
          })
        })
      } else {
        // 重置所有
        state.filter.forEach((item: FilterItem) => {
          item.options?.forEach((option: FilterItem) => {
            option.options?.forEach((option: FilterItem) => {
              option.filterState = FilterState.Off
            })
          })
        })
      }
      if (powerResourcesIndex > -1 && resourceIndex > -1 && thirdChargeStationIndex > -1) {
        if (
          zoomLevel.value > DEFAULT_COUNTRY_ZOOM_LEVEL &&
          zoomLevel.value < SHOW_THIRD_ZOOM_LEVEL
        ) {
          state.filter[powerResourcesIndex].options[resourceIndex].options[
            thirdChargeStationIndex
          ].filterState = FilterState.Disabled // 三方桩筛选disable状态
        }
      }
    }

    /**
     * 重置所有筛选
     */
    props.componentStore.bus.on('filterReset', () => {
      if (props.componentStore.filterChanged) {
        state.filterStateBeforeDisabled = FilterState.Off
        resetAllFilter()
        props.componentStore.bus.emit('initResourceFilter', {
          filter: state.filter,
          thirdSourceIndex: state.thirdSourceIndex,
        }) // 给dotRender传递筛选数据
        // onFilterChange()
      }
    })
    const resetAllFilter = () => {
      state.filter = JSON.parse(state.filterRawData)
      props.componentStore.$patch({ filter: state.filter }) // 将数据同步写入componentStore.filter
    }

    /**
     * 确认第二层的筛选
     */
    const onConfirm = () => {
      state.confirmFilterChange = true
      if (
        state.stashFilter != JSON.stringify(state.filter[state.filterIconSelectedIndex].options)
      ) {
        // 数据没变化 不触发filterChange
        onFilterChange()
      }
      closeFirstLevelFilterPan() // 关闭筛选看板
    }

    /**
     * 重置第二层的筛选
     */
    const onReset = () => {
      resetSecondFilter(state.filterIconSelectedIndex)
    }

    /**
     * 关闭第二层级筛选看板
     * @returns
     */
    const closeFirstLevelFilterPan = () => {
      if (!state.confirmFilterChange) {
        // 没有点击确定按钮还原筛选状态
        restoreFilterFn(state.filterIconSelectedIndex)
      }
      state.confirmFilterChange = false
      state.filterIconSelectedIndex = -1
      // props.componentStore.bus.emit('filterPanChange', { open: false })
      props.componentStore.$patch({
        filterPanel: false,
      })
    }

    /**
     * 关闭看板
     */
    const closeFilterPanel = () => {
      if (state.filterIconSelectedIndex == -1) {
        return
      }
      if (!state.confirmFilterChange) {
        // 没有点击确定按钮还原筛选状态
        restoreFilterFn(state.filterIconSelectedIndex)
      }
      state.confirmFilterChange = false
      state.filterIconSelectedIndex = -1
    }
    props.componentStore.bus.on('closeFilterPanel', closeFilterPanel)

    /**
     * 通过地图缩放级别改变三方点的diabled状态
     * @param level
     */
    const changeFilterByZoomLevel = (zoomLevel: number) => {
      if (powerResourcesIndex == -1 || resourceIndex == -1 || thirdChargeStationIndex == -1) {
        return
      }
      // 从country到route，从route到country需要单独考虑三方的筛选
      if (zoomLevel > DEFAULT_COUNTRY_ZOOM_LEVEL && zoomLevel < SHOW_THIRD_ZOOM_LEVEL) {
        // 路线模式 和 无三方点大ICON 下 三方点禁用
        if (
          state.filter[powerResourcesIndex].options[resourceIndex].options[thirdChargeStationIndex]
            .filterState != FilterState.Disabled
        ) {
          // disabled前三方点的状态不为disabled时，缓存状态，切触发filterChange
          state.filter[powerResourcesIndex].options[resourceIndex].options[
            thirdChargeStationIndex
          ].filterState = FilterState.Disabled
          if (zoomLevel <= DEFAULT_DISTRICT_ZOOM_LEVEL) {
            // 城市级别下，不用zoomend触发filterChange
            onFilterChange()
          }
        }
      } else {
        // 全国模式 和 有三方点大ICON下 三方点启用
        // 启用状态为禁用之前状态
        if (
          zoomLevel >= SHOW_THIRD_ZOOM_LEVEL &&
          state.filterStateBeforeDisabled === FilterState.Disabled
        ) {
          // 20240523 解决在没有三方资源的层级选中自营桩，缩放地图到有三方资源的层级，此时无法筛选三方桩
          state.filter[powerResourcesIndex].options[resourceIndex].options[
            thirdChargeStationIndex
          ].filterState = FilterState.Off
          state.filterStateBeforeDisabled = FilterState.Off
        } else if (
          state.filter[powerResourcesIndex].options[resourceIndex].options[thirdChargeStationIndex]
            .filterState != state.filterStateBeforeDisabled
        ) {
          // 回到取消disabled前的状态
          state.filter[powerResourcesIndex].options[resourceIndex].options[
            thirdChargeStationIndex
          ].filterState = state.filterStateBeforeDisabled
          if (zoomLevel <= DEFAULT_DISTRICT_ZOOM_LEVEL) {
            // 城市级别下，不用zoomend触发filterChange
            onFilterChange()
          }
        }
      }
      // 20240606 修复zoom到三方桩禁用状态下，点击数据看板，再关闭数据看板，点击筛选icon，三方桩变为可筛选的问题
      stashFilterFn(powerResourcesIndex)
    }

    /**
     * 通过接口获取filter数据
     */
    const initFilterState = async () => {
      if (props.componentStore.filter?.length && props.componentStore.filterRawData) {
        // 缓存筛选接口数据，只请求一次
        state.filter = props.componentStore.filter
        state.filterRawData = props.componentStore.filterRawData
      } else {
        // 通过配置读取filter数据
        const res: ServiceResponse<unknown> = await getFilterData(
          componentsConfig?.filter?.properties?.items || ''
        )
        if (res.isValid() && res.data) {
          state.filter = res.data as FilterItem[]
          const filterNameMapTemp = {} as any
          state.filter?.forEach((item: FilterItem) => {
            filterNameMapTemp[item.key] = item.name
          })
          props.componentStore.$patch({ filterNameMap: filterNameMapTemp })
          state.filter.forEach((item: FilterItem, index: number) => {
            if (componentsConfig?.filter?.properties?.default_selected_status) {
              item.filterState = componentsConfig?.filter?.properties?.default_selected_status[
                index
              ]
                ? FilterState.On
                : FilterState.Off
              item.selected = !!componentsConfig?.filter?.properties?.default_selected_status[index]
            } else {
              // 没设定默认选项，默认全选
              item.filterState = FilterState.On
              item.selected = false
            }
          })
        }
        props.componentStore.$patch({ filterRawData: JSON.stringify(state.filter) })
        state.filterRawData = props.componentStore.filterRawData
        props.componentStore.$patch({ filter: state.filter }) // 将数据同步写入componentStore.filter
      }
    }

    /**
     * 记录三方disabled前的状态
     */
    const recordThirdFilterState = () => {
      if (powerResourcesIndex > -1 && resourceIndex > -1 && thirdChargeStationIndex > -1) {
        state.filterStateBeforeDisabled = state.filter[powerResourcesIndex].options[resourceIndex]
          .options[thirdChargeStationIndex].filterState as FilterState // 三方桩保留原来筛选状态
      }
    }

    /**
     * 试驾点和蔚来门店在全国模式下不可点击
     * @param index
     * @returns
     */
    const disableFilterIconClick = (index: number) => {
      return (
        state.filter[index].key != FilterKey.RechargeCS &&
        state.filter[index].key != FilterKey.Recharge &&
        zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL
      )
    }

    /**
     * 不展示筛选按钮
     * @param index
     * @returns
     */
    const hideFilterIconClick = (index: number) => {
      if (componentsConfig?.filter?.properties?.hide_second_filter) {
        return componentsConfig?.filter?.properties?.hide_second_filter.includes(
          state.filter[index].key
        )
      } else {
        // 兜底服务中心和换电站不展示二级筛选
        return (
          state.filter[index].key === FilterKey.ServiceCenter ||
          state.filter[index].key === FilterKey.RechargePS
        )
      }
    }

    const filterResourceIconRender = (type: string) => {
      switch (type) {
        case FilterKey.Recharge:
          // 20240523 兼容处理
          return (
            <ns-icon
              name={(process.config as any).icon.icon_charging_pile}
              color="#79A0C5"
              class={styles.resource}
            />
          )
          break
        case FilterKey.RechargePS:
          return (
            <ns-icon
              name={(process.config as any).icon.icon_powerswap_staion}
              color="#295279"
              class={styles.resource}
            />
          )
          break
        case FilterKey.RechargeCS:
          return (
            <ns-icon
              name={(process.config as any).icon.icon_charging_pile}
              color="#79A0C5"
              class={styles.resource}
            />
          )
          break
        case FilterKey.ServiceCenter:
          return (
            <ns-icon
              name={(process.config as any).icon.icon_service_center}
              color="#FCCE87"
              class={styles.resource}
            />
          )
          break
        case FilterKey.NioStore:
          return (
            <ns-icon
              name={(process.config as any).icon.icon_nio_house}
              color="#FFB47A"
              class={styles.resource}
            />
          )
          break
        case FilterKey.TestDrive:
          return <ns-icon name="icon-drive-experience" color="#57A4FD" class={styles.resource} />
          break

        default:
          break
      }
    }

    /**
     * 打开筛选popup看板
     */
    const showFilterPopupView = () => {
      state.isShow = true
    }
    client === 'mobile' && props.componentStore.bus.on('showFilterPopupView', showFilterPopupView)

    const closePopup = () => {
      state.isShow = false
      emit('closePopup')
    }

    // ------------暴露出去的方法--------------
    /**
     * filter change向外暴露事件
     */
    const onFilterChange = () => {
      props.componentStore.$patch({ filter: state.filter }) // 将数据同步写入componentStore.filter
      if (props.componentStore.viewMode === MapViewMode.Init && !state.stopEmitFilterChanged) {
        // 只有初始状态才能触发filterChanged
        emit('filterChanged', state.filter)
        props.componentStore.bus.emit('filterChanged', {
          filter: state.filter,
          thirdSourceIndex: state.thirdSourceIndex,
        })
      }
      state.stopEmitFilterChanged = false
    }

    const zoomEnd = () => {
      zoomLevel.value = mapInstance.value?.getZoom()
      changeFilterByZoomLevel(zoomLevel.value)
    }
    const zoomStart = () => {}
    expose({
      zoomEnd,
      zoomStart,
      onFilterChange,
    })

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEnd, false)
      mapInstance.value?.addEventListener('zoomstart', zoomStart, false)
    }

    // // unmounted时摧毁相关事件监听
    // const destroyEventListener = () => {
    //   mapInstance.value.removeEventListener('zoomend', zoomEnd, false)
    //   mapInstance.value.removeEventListener('zoomstart', zoomStart, false)
    // }

    onUnmounted(() => {
      // destroyEventListener()
      // ! 一定要加上off，因为组件会不停的注销和挂载，如果不off，会导致多次注册事件
      props.componentStore.bus.off('triggerFilter')
      props.componentStore.bus.off('resetSecondFilter')
      props.componentStore.bus.off('filterReset')
      props.componentStore.bus.off('closeFilterPanel')
      props.componentStore.bus.off('showFilterPopupView')
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = async () => {
      // mapInstance.value = mapContextRef.value?.getMap()
      await initFilterState()
      findThirdIndex()
      recordThirdFilterState()
      initEventListener()
      zoomEnd()
      state.filterDataReady = true
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          // mapContextRef.value = props.mapContextRef
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => state.filter,
      () => {
        if (state.filterRawData) {
          props.componentStore.$patch({
            filterChanged: state.filterRawData != JSON.stringify(state.filter),
          })
        }
      },
      {
        deep: true,
      }
    )

    const filterVisible = computed(() => {
      return (
        componentsConfig?.filter?.show &&
        componentsConfig?.common?.show &&
        !props.xinfaView &&
        props.componentStore.showHandleBox &&
        !props.componentStore.showSummary &&
        props.componentStore.viewMode == MapViewMode.Init
      )
    })

    return () => (
      <div
        style={{
          visibility: filterVisible.value ? 'visible' : 'hidden',
        }}
      >
        {client !== 'mobile' ? (
          <div class={styles.filter}>
            {state.filterDataReady && (
              <div class={[styles.filterFixed]}>
                {state.filter.map((item: FilterItem, index: number) => (
                  <div class={styles.filterBg}>
                    <div class={[styles.filterWrap, item.selected && styles.filterOn]}>
                      <van-checkbox
                        vModel={item.selected}
                        vSlots={{
                          icon: () => filterResourceIconRender(item.key),
                        }}
                        onChange={(checked: boolean) => {
                          firstLevelFilterChanged(checked, index)
                        }}
                      >
                        {item.name}
                      </van-checkbox>
                      {hideFilterIconClick(index) || (
                        <>
                          {selectedNumList.value[index] > 0 ? (
                            <van-badge content={selectedNumList.value[index]}>
                              <ns-icon
                                class={[
                                  styles.filterIcon,
                                  disableFilterIconClick(index) && styles.filterIconDisable,
                                ]}
                                name={(process.config as any).icon.filter_block_outline}
                                color={
                                  !disableFilterIconClick(index)
                                    ? (process.config as any).style.filterIconColor
                                    : '#CED0D8'
                                }
                                size={px2rem(16)}
                                onClick={() => openFirstLevelFilterPan(index)}
                              />
                            </van-badge>
                          ) : (
                            <ns-icon
                              class={[
                                styles.filterIcon,
                                !item.selected && styles.filterIconDisable,
                                disableFilterIconClick(index) && styles.filterIconDisable,
                              ]}
                              name={(process.config as any).icon.filter_block_outline}
                              color={
                                !disableFilterIconClick(index)
                                  ? item.selected
                                    ? (process.config as any).style.filterIconColor
                                    : 'black'
                                  : '#CED0D8'
                              }
                              size={px2rem(16)}
                              onClick={() => openFirstLevelFilterPan(index)}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {state.filterIconSelectedIndex != -1 && (
              <div class={styles.childFilter}>
                <div class={styles.header}>
                  <div class={styles.title}>{state.filter[state.filterIconSelectedIndex].name}</div>
                  <ns-icon
                    class={styles.closeBtn}
                    name={(process.config as any).icon.close_big_outline}
                    size={px2rem(21)}
                    onClick={closeFirstLevelFilterPan}
                  />
                </div>
                <div class={styles.content}>
                  {state.filter[state.filterIconSelectedIndex].options?.map((item: FilterItem) => (
                    <div>
                      {item.name && <div class={styles.subTitle}>{item.name}</div>}
                      <div class={styles.options}>
                        {item.options?.map((option: FilterItem) => (
                          <div
                            class={[
                              styles.option,
                              option.filterState === FilterState.On && styles.selected,
                              option.filterState === FilterState.Disabled && styles.disabled,
                            ]}
                            onClick={() => {
                              if (option.filterState != FilterState.Disabled) {
                                option.filterState =
                                  option.filterState === FilterState.On
                                    ? FilterState.Off
                                    : FilterState.On
                              }
                              recordThirdFilterState()
                            }}
                          >
                            {option.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div class={styles.footer}>
                  <van-button round class={styles.reset} onClick={onReset}>
                    重置
                  </van-button>
                  <van-button round class={styles.confirm} onClick={onConfirm}>
                    确认
                  </van-button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Popup
            show={state.isShow}
            onClickOverlay={closePopup}
            position="bottom"
            class={styles.popupView}
          >
            <div class={styles.header}>
              <div class={styles.title}>筛选</div>
              <div class="closeIcon">
                <ns-icon
                  name={(process.config as any).icon.close_big_outline}
                  onClick={closePopup}
                  size="27"
                  color="#696D7F"
                />
              </div>
            </div>
            <div class={styles.content}>
              <div class={[styles.filterFixed]}>
                {state.filter.map((item: FilterItem, index: number) => (
                  <div class={styles.filterBg}>
                    <div class={[styles.filterWrap, item.selected && styles.filterOn]}>
                      <van-checkbox
                        v-model={item.selected}
                        v-slots={{
                          icon: () => filterResourceIconRender(item.key),
                        }}
                        onChange={(checked: boolean) => {
                          firstLevelFilterChanged(checked, index)
                        }}
                      >
                        {item.name}
                      </van-checkbox>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Popup>
        )}
      </div>
    )
  },
})
